import { useCallback, useEffect, useRef, useState } from "react";
import { useReports } from "../../../../hooks/useReports";
import {
  RunReportV2Request,
  RunReportV2Response,
} from "../../../../types/reportsV2";
import { requestKeyOf } from "../model/viewConfig";
import { isAbortError, RunError, toRunError } from "../model/runErrors";

/** Wait this long after the last query-changing edit before running. */
export const RUN_DEBOUNCE_MS = 300;

export interface UseReportRunOptions {
  salonId: string;
  reportId: string;
  /** The query part of the current (draft) view — e.g. `runRequestOf(view)`
   *  for a table report. Edits that leave it unchanged never refetch. */
  request: RunReportV2Request;
  debounceMs?: number;
}

export interface ReportRunState<T = RunReportV2Response> {
  /** Latest successful result (kept while a newer run is in flight or failed,
   *  so facets and dynamic columns stay available). */
  data: T | null;
  loading: boolean;
  error: RunError | null;
  /** Run again now with the current view. */
  retry: () => void;
}

/**
 * Runs a v2 report (table or summary — `T` is its response) whenever the
 * request changes: the first run starts immediately, later changes are
 * debounced, a superseded request is aborted, and any response that isn't
 * for the latest request is ignored.
 */
export function useReportRun<T = RunReportV2Response>({
  salonId,
  reportId,
  request,
  debounceMs = RUN_DEBOUNCE_MS,
}: UseReportRunOptions): ReportRunState<T> {
  const { runSalonReportV2 } = useReports();
  const key = requestKeyOf(request);
  const requestRef = useRef(request);
  requestRef.current = request;

  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<RunError | null>(null);
  const [attempt, setAttempt] = useState(0);

  const latestRequest = useRef(0);
  const controller = useRef<AbortController | null>(null);
  const hasRun = useRef(false);
  const runNow = useRef(false);

  useEffect(() => {
    // Invalidate whatever is in flight: its answer is for an older view.
    const requestId = ++latestRequest.current;
    controller.current?.abort();
    setLoading(true);
    const delay = hasRun.current && !runNow.current ? debounceMs : 0;
    runNow.current = false;

    const timer = setTimeout(() => {
      hasRun.current = true;
      const abort = new AbortController();
      controller.current = abort;
      runSalonReportV2<T>(salonId, reportId, requestRef.current, {
        signal: abort.signal,
      })
        .then((result) => {
          if (requestId !== latestRequest.current) return;
          setData(result);
          setError(null);
          setLoading(false);
        })
        .catch((e: unknown) => {
          if (requestId !== latestRequest.current || isAbortError(e)) return;
          console.error("Failed to run report:", e);
          setError(toRunError(e));
          setLoading(false);
        });
    }, delay);

    return () => clearTimeout(timer);
  }, [key, attempt, salonId, reportId, debounceMs, runSalonReportV2]);

  // Abort the last request when the canvas unmounts.
  useEffect(() => () => controller.current?.abort(), []);

  const retry = useCallback(() => {
    runNow.current = true;
    setAttempt((n) => n + 1);
  }, []);

  return { data, loading, error, retry };
}
